import { Table } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { MRT_ColumnDef } from "material-react-table";
import React, { useMemo } from "react";
import { TGetCompanyOutputDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { useGetMainIndustryList } from "@/api/services/global/master";
import { findLabelByValue } from "@/utils/object";
import { getFullAddress, truncateWithEllipsis } from "@/utils/string";
import { MAX_COMPANY_DESCRIPTION_AND_MEMO_LENGTH, MAX_COMPANY_NAME_LENGTH } from "@/features/company/constants/length";
import { typography } from "@/theme/typography";
import { divider } from "@/theme/colors";

type TCompanyAfterProps = {
  rows: TGetCompanyOutputDto[];
  isLoading: boolean;
};

export const CompanyAfterImport = ({ rows, isLoading }: TCompanyAfterProps) => {
  const { mainIndustryOptions } = useGetMainIndustryList();

  const ItemCell = (firstLabel: string | null | undefined, secondLabel: string | null | undefined) => {
    const isEmpty = !firstLabel && !secondLabel;

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        {isEmpty ? (
          <p style={typography.body14}>{EMPTY_STR.TEXT}</p>
        ) : (
          <>
            <p style={typography.body14}>{firstLabel ?? ""}</p>
            <p style={typography.cap12}>{secondLabel ?? ""}</p>
          </>
        )}
      </div>
    );
  };

  const columns = useMemo<MRT_ColumnDef<TGetCompanyOutputDto>[]>(
    () => [
      {
        header: "行",
        Cell: ({ row }) => {
          return row.index + 1;
        },
        size: 14,
      },
      {
        header: "企業番号",
        Cell: ({ row }) => row.original.managementNumber || EMPTY_STR.TEXT,
        size: 88,
      },
      {
        header: "企業名",
        accessorKey: "companyName",
        size: 166,
        Cell: ({ row }) => truncateWithEllipsis(String(row.original.companyName) ?? "-", MAX_COMPANY_NAME_LENGTH),
      },
      {
        header: "業種",
        size: 172,
        Cell: ({ row }) => findLabelByValue(mainIndustryOptions, row.original.mainIndustryId) || EMPTY_STR.TEXT,
      },
      {
        header: "設立年",
        size: 78,
        Cell: ({ row }) => (row.original.establishmentYear ? `${row.original.establishmentYear ?? 0}年` : EMPTY_STR.TEXT),
      },
      {
        header: "資本金",
        size: 92,
        Cell: ({ row }) => (row.original.capital ? `${row.original.capital ?? 0}円` : EMPTY_STR.TEXT),
      },
      {
        header: "従業員数",
        accessorKey: "numberOfEmployees",
        Cell: ({ row }) => row.original.numberOfEmployees || EMPTY_STR.TEXT,
        size: 104,
      },
      {
        header: "代表者",
        Cell: ({ row }) => ItemCell(row.original.representativeName, row.original.representativePosition),
        size: 92,
      },
      {
        header: "電話番号",
        size: 138,
        Cell: ({ row }) => ItemCell(row.original.telNumber, row.original.faxNumber ? `(FAX) ${row.original.faxNumber}` : null),
      },
      {
        header: "メールアドレス",
        size: 130,
        Cell: ({ row }) => row.original.contactEmail || EMPTY_STR.TEXT,
      },
      {
        header: "所在地",
        size: 116,
        Cell: ({ row }) =>
          ItemCell(
            row.original.zipCode && `〒${row.original.zipCode}`,
            getFullAddress({
              zipCode: "",
              province: row.original.prefecture,
              municipality: row.original.city,
              address: row.original.address,
            }),
          ),
      },
      {
        header: "担当者",
        size: 74,
        Cell: ({ row }) => ItemCell(row.original.contactPersonName, row.original.contactPersonJobTitle),
      },
      {
        header: "担当者電話番号",
        size: 130,
        Cell: ({ row }) => row.original.contactPersonPhone || EMPTY_STR.TEXT,
      },
      {
        header: "担当者メール",
        size: 179,
        Cell: ({ row }) => row.original.contactPersonEmail || EMPTY_STR.TEXT,
      },
      {
        header: "事業内容・企業特性",
        size: 270,
        Cell: ({ row }) => truncateWithEllipsis(row.original.companyDescription || EMPTY_STR.TEXT, MAX_COMPANY_DESCRIPTION_AND_MEMO_LENGTH),
      },
      {
        header: "メモ",
        size: 158,
        Cell: ({ row }) => truncateWithEllipsis(row.original.memo || EMPTY_STR.TEXT, MAX_COMPANY_DESCRIPTION_AND_MEMO_LENGTH),
      },
    ],
    [mainIndustryOptions],
  );

  const { table } = useTable({ columns, rows });

  return <Table table={table} isLoading={isLoading} borderColor={divider.high} height="calc(100vh - 620px)" />;
};
